import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, GOOGLE_CLIENT_ID } from '../config';
import { useAuth } from '../context/AuthContext';
import { auth } from '../firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';


// Same logo as Header
const ACOInsightsLogo = () => (
  <svg width="72" height="72" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="loginBarGradient" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#60a5fa" stopOpacity="1" />
        <stop offset="100%" stopColor="#38bdf8" stopOpacity="0.9" />
      </linearGradient>
    </defs>
    <rect x="10" y="26" width="4" height="9" rx="2" fill="url(#loginBarGradient)"/>
    <rect x="16" y="22" width="4" height="13" rx="2" fill="url(#loginBarGradient)"/>
    <rect x="22" y="24" width="4" height="11" rx="2" fill="url(#loginBarGradient)"/>
    <rect x="28" y="19" width="4" height="16" rx="2" fill="url(#loginBarGradient)"/>
    <rect x="34" y="21" width="4" height="14" rx="2" fill="url(#loginBarGradient)"/>
    <path d="M 8 12 L 12 12 L 14 9 L 16 15 L 18 11 L 20 12 L 40 12"
          stroke="#10b981"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.95"/>
    <circle cx="14" cy="9" r="1.5" fill="#10b981" opacity="0.8"/>
  </svg>
);

function Login({ onLoginSuccess }) {
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authTab, setAuthTab] = useState('email'); // 'google' | 'email'
  const [emailForm, setEmailForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);

  const [googleLoaded, setGoogleLoaded] = useState(false);

  // Load Google Sign-In script once
  useEffect(() => {
    // If already loaded (hot reload), just set ready
    if (window.google) { setGoogleLoaded(true); return; }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setGoogleLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Render the Google button whenever we switch to the Google tab (and SDK is ready)
  useEffect(() => {
    if (!googleLoaded || authTab !== 'google') return;
    if (!window.google) return;
    const el = document.getElementById('google-signin-button');
    if (!el) return;
    el.innerHTML = ''; // clear any previous render
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleSignIn
    });
    window.google.accounts.id.renderButton(el, {
      theme: 'outline', size: 'large', type: 'standard', text: 'signin_with', width: 320
    });
  }, [googleLoaded, authTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Google OAuth Flow ────────────────────────────────────────────────────
  const handleGoogleSignIn = async (response) => {
    try {
      setIsLoading(true);
      setError(null);
      if (!response || !response.credential) {
        throw new Error('No credential received from Google. Please try again.');
      }
      const backendResponse = await axios.post(
        `${API_BASE_URL}/auth/google-login`,
        { google_token: response.credential },
        { timeout: 15000 }
      );
      const { user, token } = backendResponse.data;
      if (!user || !token) throw new Error('Invalid response from server.');
      localStorage.setItem('jwt_token', token);
      localStorage.setItem('user_info', JSON.stringify(user));
      login(user, token);
      if (onLoginSuccess) onLoginSuccess(user);
    } catch (err) {
      let msg = 'Google sign-in failed. Please try again.';
      if (err.response?.data?.detail) msg = err.response.data.detail;
      else if (err.message) msg = err.message;
      setError(msg);
      setIsLoading(false);
    }
  };

  // ─── Email / Password Flow (Firebase Auth) ───────────────────────────────
  const handleEmailPasswordSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      // Password goes DIRECTLY to Firebase — never touches our server (HIPAA compliant)
      const userCredential = await signInWithEmailAndPassword(
        auth,
        emailForm.email.trim(),
        emailForm.password
      );
      // Get Firebase ID token (signed JWT, not the password)
      const idToken = await userCredential.user.getIdToken();
      // Exchange Firebase ID token for our app JWT
      const backendResponse = await axios.post(
        `${API_BASE_URL}/auth/email-login`,
        { firebase_id_token: idToken },
        { timeout: 15000 }
      );
      const { user, token } = backendResponse.data;
      if (!user || !token) throw new Error('Invalid response from server.');
      localStorage.setItem('jwt_token', token);
      localStorage.setItem('user_info', JSON.stringify(user));
      login(user, token);
      if (onLoginSuccess) onLoginSuccess(user);
    } catch (err) {
      console.error('[Login] Sign-in error:', err.code, err.message, err);
      // HIPAA: Generic messages — do not reveal whether the email exists
      let msg = 'Sign in failed. Please check your credentials and try again.';
      if (err.code === 'auth/invalid-credential' ||
          err.code === 'auth/invalid-login-credentials' ||
          err.code === 'auth/user-not-found' ||
          err.code === 'auth/wrong-password') {
        msg = 'Incorrect email or password. Please try again.';
      } else if (err.code === 'auth/too-many-requests') {
        msg = 'Account temporarily locked after too many failed attempts. Please try again later.';
      } else if (err.code === 'auth/user-disabled') {
        msg = 'This account has been disabled. Please contact your administrator.';
      } else if (err.code === 'auth/network-request-failed') {
        msg = 'Network error. Please check your connection and try again.';
      } else if (err.code === 'auth/invalid-api-key') {
        msg = 'Configuration error. Please contact your administrator.';
      } else if (err.code === 'auth/unauthorized-domain') {
        msg = 'This domain is not authorized. Please contact your administrator.';
      } else if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      } else if (err.message) {
        msg = `Sign in failed: ${err.message}`;
      }
      setError(msg);
      setIsLoading(false);
    }
  };

  const handleFormChange = (field) => (e) => {
    setEmailForm(prev => ({ ...prev, [field]: e.target.value }));
    if (error) setError(null);
  };

  const switchTab = (tab) => {
    setAuthTab(tab);
    setError(null);
  };



  return (
    <div style={styles.container}>
      <style>{`
        #google-signin-button > div { width: 100% !important; margin: 0 auto !important; }
        .auth-tab-btn { transition: all 0.2s ease; }
        .auth-tab-btn:hover { opacity: 0.85; }
        .email-input:focus { outline: none; border-color: #1a1a1a !important; box-shadow: 0 0 0 3px rgba(26,26,26,0.08); }
        .sign-in-btn:hover:not(:disabled) { background: #333 !important; transform: translateY(-1px); }
        .sign-in-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        /* ── Mobile responsive overrides ──────────────────────────── */
        @media (max-width: 520px) {
          .login-card {
            margin: auto 10px !important;
            border-radius: 14px !important;
          }
          .login-card-header {
            padding: 32px 20px 20px !important;
          }
          .login-tab-row {
            margin: 14px 14px 0 !important;
          }
          .login-content {
            padding: 20px 20px 0 !important;
          }
          .login-error-alert {
            margin: 14px 14px 0 !important;
          }
          .login-footer {
            padding: 20px 20px !important;
          }
        }
        @media (max-width: 360px) {
          .login-card-header {
            padding: 24px 14px 16px !important;
          }
          .login-tab-row {
            margin: 12px 12px 0 !important;
          }
          .login-content {
            padding: 16px 14px 0 !important;
          }
          .login-footer {
            padding: 16px 14px !important;
          }
        }
      `}</style>

      <div style={styles.card} className="login-card">
        {/* Header */}
        <div style={styles.header} className="login-card-header">
          <div style={styles.logoContainer}>
            <div style={styles.logo}><ACOInsightsLogo /></div>
            <h1 style={styles.title}>ACO Insights</h1>
          </div>
          <p style={styles.tagline}>Healthcare Analytics &amp; Quality Reporting</p>
        </div>

        {/* Error Banner - only show on Google tab */}
        {error && authTab === 'google' && (
          <div style={styles.errorAlert} className="login-error-alert">
            <span style={styles.errorIcon}>✕</span>
            <div style={styles.errorContent}>
              <p style={styles.errorTitle}>Sign In Error</p>
              <p style={styles.errorMessage}>{error}</p>
            </div>
          </div>
        )}

        {/* Auth Tabs */}
        <div style={styles.tabRow} className="login-tab-row">
          <button className="auth-tab-btn" onClick={() => switchTab('google')}
            style={{...styles.tabBtn, ...(authTab === 'google' ? styles.tabBtnActive : styles.tabBtnInactive)}}>
            G&nbsp;&nbsp;Google
          </button>
          <button className="auth-tab-btn" onClick={() => switchTab('email')}
            style={{...styles.tabBtn, ...(authTab === 'email' ? styles.tabBtnActive : styles.tabBtnInactive)}}>
            🔑&nbsp;&nbsp;Email &amp; Password
          </button>
        </div>

        {/* Tab Content */}
        <div style={styles.content} className="login-content">

          {/* Google Tab */}
          {authTab === 'google' && (
            <div style={styles.authSection}>
              <p style={styles.panelHint}>Sign in with your organization Google account</p>
              <div style={styles.oauthButtonWrapper}>
                <div id="google-signin-button" style={styles.googleButtonContainer}></div>
              </div>
              {isLoading && (
                <div style={styles.loadingRow}>
                  <span>⏳</span>
                  <span style={styles.loadingText}>Signing in...</span>
                </div>
              )}
            </div>
          )}

          {/* Email & Password Tab */}
          {authTab === 'email' && (
            <div style={styles.authSection}>
              <p style={styles.panelHint}>Sign in with your email address and password</p>
              <form onSubmit={handleEmailPasswordSignIn} style={styles.form} autoComplete="off">
                <div style={styles.fieldGroup}>
                  <label htmlFor="login-email" style={styles.label}>Email Address</label>
                  <input id="login-email" className="email-input" type="email" required
                    autoComplete="new-password" placeholder="you@sdcorner.com"
                    value={emailForm.email} onChange={handleFormChange('email')}
                    disabled={isLoading} style={styles.input} />
                </div>
                <div style={styles.fieldGroup}>
                  <label htmlFor="login-password" style={styles.label}>Password</label>
                  <div style={styles.passwordWrapper}>
                    <input id="login-password" className="email-input"
                      type={showPassword ? 'text' : 'password'} required
                      autoComplete="new-password" placeholder="••••••••"
                      value={emailForm.password} onChange={handleFormChange('password')}
                      disabled={isLoading}
                      style={{...styles.input, paddingRight: '48px'}} />
                    <button type="button" onClick={() => setShowPassword(s => !s)}
                      style={styles.togglePwBtn} tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>
                <button type="submit" className="sign-in-btn"
                  disabled={isLoading || !emailForm.email || !emailForm.password}
                  style={styles.signInBtn}>
                  {isLoading ? '⏳ Signing in...' : 'Sign In'}
                </button>

                {/* Inline error — visible right below the button */}
                {error && authTab === 'email' && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px 14px',
                    background: '#fef2f2',
                    border: '1px solid #fca5a5',
                    borderRadius: '8px',
                    color: '#b91c1c',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                  }}>
                    <span style={{ flexShrink: 0, fontWeight: 'bold' }}>✕</span>
                    <span>{error}</span>
                  </div>
                )}
              </form>
              <p style={styles.adminNote}>Don&apos;t have an account? Contact your administrator.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer} className="login-footer">
          <p style={styles.footerText}>🔒 Secure HIPAA-Compliant Access</p>
        </div>
      </div>

      <div style={styles.securityFooter}>
        <p style={styles.securityText}>
          Authorized users only · This system is monitored · All access is logged
        </p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex', flexDirection: 'column', minHeight: '100vh',
    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
    color: '#1f2937',
  },
  card: {
    display: 'flex', flexDirection: 'column',
    background: 'rgba(255, 255, 255, 0.25)', backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '20px', boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
    width: '100%', maxWidth: '480px', margin: 'auto',
  },
  header: {
    padding: '48px 40px 28px', textAlign: 'center',
    borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
  },
  logoContainer: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '8px' },
  logo: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  logoIcon: { fontSize: '32px', lineHeight: '1' },
  title: { fontSize: '32px', fontWeight: '700', color: '#1a1a1a', margin: 0, letterSpacing: '-0.5px' },
  tagline: { fontSize: '14px', color: '#666', margin: '8px 0 0 0', fontWeight: '500' },
  errorAlert: {
    display: 'flex', gap: '12px', backgroundColor: '#fee2e2', border: '1px solid #fca5a5',
    borderRadius: '12px', padding: '14px 16px', margin: '20px 40px 0', alignItems: 'flex-start',
  },
  errorIcon: { color: '#dc2626', fontWeight: 'bold', fontSize: '18px', flexShrink: 0 },
  errorContent: { flex: 1 },
  errorTitle: { fontSize: '13px', fontWeight: '600', color: '#991b1b', margin: '0 0 4px 0' },
  errorMessage: { fontSize: '13px', color: '#7f1d1d', margin: 0, lineHeight: '1.5' },
  tabRow: {
    display: 'flex', margin: '20px 40px 0', borderRadius: '12px', overflow: 'hidden',
    border: '1px solid rgba(0,0,0,0.1)', background: 'rgba(0,0,0,0.03)',
  },
  tabBtn: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '6px', padding: '11px 12px', border: 'none', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600',
  },
  tabBtnActive: { background: '#1a1a1a', color: '#fff' },
  tabBtnInactive: { background: 'transparent', color: '#555' },
  content: { padding: '28px 40px 0' },
  authSection: { display: 'flex', flexDirection: 'column', gap: '12px' },
  panelHint: { fontSize: '13px', color: '#666', margin: '0 0 4px 0', textAlign: 'center' },
  oauthButtonWrapper: { marginBottom: '4px', display: 'flex', justifyContent: 'center' },
  googleButtonContainer: { display: 'flex', justifyContent: 'center', width: '100%' },
  loadingRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '8px' },
  loadingText: { fontSize: '14px', color: '#555' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#374151' },
  input: {
    width: '100%', padding: '11px 14px', borderRadius: '10px', border: '1.5px solid #d1d5db',
    fontSize: '15px', color: '#1a1a1a', background: 'rgba(255,255,255,0.8)',
    boxSizing: 'border-box', transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  passwordWrapper: { position: 'relative' },
  togglePwBtn: {
    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px',
    color: '#9ca3af', padding: '4px', lineHeight: '1',
  },
  signInBtn: {
    width: '100%', padding: '13px', borderRadius: '10px', border: 'none',
    background: '#1a1a1a', color: '#fff', fontSize: '15px', fontWeight: '600',
    cursor: 'pointer', marginTop: '4px', transition: 'background 0.2s, transform 0.15s',
  },
  adminNote: { fontSize: '12px', color: '#9ca3af', textAlign: 'center', margin: '4px 0 0 0' },
  footer: {
    padding: '24px 40px', borderTop: '1px solid rgba(255,255,255,0.2)',
    textAlign: 'center', marginTop: '24px',
  },
  footerText: { fontSize: '12px', color: '#6b7280', margin: 0, fontWeight: '500' },
  securityFooter: { textAlign: 'center', padding: '14px 20px', background: 'rgba(0,0,0,0.05)' },
  securityText: { fontSize: '11px', color: '#666', margin: 0 },
};

export default Login;
